import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserAgent, Registerer, RegistererState, SessionState } from "sip.js";
import { useAuth } from "./AuthContext";
import { d } from "../lib/dialer";
import { useRemoteAudioLevel } from "../hooks/useAudioLevel";
import { createSiren } from "../lib/transferAlertSiren";
import { shouldShowTransferAlert, pickTransferPreview } from "../lib/transferAlert";
import TransferAlertOverlay from "../components/dialer/TransferAlertOverlay";

// Teléfono REAL del puesto (Etapa 3). SIP.js solo es el AUDIO: registra la extensión del puesto en la central y
// contesta SOLO las llamadas que la central le pasa (la central es quien marca, mantiene en espera, transfiere...).
// Aquí no se decide nada de negocio: el backend (ARI) es la única autoridad de cada llamada.
//
// Vive por encima de las páginas (se monta junto al resto de contextos), así que cambiar de módulo no corta
// la llamada. Una sola pestaña por usuario tiene el teléfono (Web Locks): registrar la misma extensión en dos
// pestañas haría que una desplazara a la otra.

const Ctx = createContext(null);
export const usePhone = () => useContext(Ctx);

// admin/gerente: solo reciben teléfono si el servidor los trae como asistente asignado de un puesto (/phone/config); si no, quedan "sin_telefono".
const PHONE_ROLES = ["asistente", "bilingue", "admin", "gerente"];
const LOCK_NAME = "commandix-phone";

export function PhoneProvider({ children }) {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const enabled = !!user && PHONE_ROLES.includes(role);

  // estado: apagado | sin_telefono | otra_pestana | conectando | listo | reconectando | error
  const [status, setStatus] = useState("apagado");
  const [centralOk, setCentralOk] = useState(null);   // null = aún no se sabe / no hay telefonía real   // lo actualiza la página del Centro de Llamadas; lo lee la señal del teléfono
  const [info, setInfo] = useState(null);       // { extension, station_name }
  const [inCall, setInCall] = useState(false);
  const [muted, setMuted] = useState(false);
  const [mic, setMic] = useState("desconocido"); // granted | prompt | denied | desconocido
  const [error, setError] = useState("");
  const [incoming, setIncoming] = useState(null);  // { kind, from, phone, client, invitation }
  const audioRef = useRef(null);
  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const releaseRef = useRef(null);
  const retryRef = useRef(0);
  const [retry, setRetry] = useState(0);

  const refreshMic = useCallback(async () => {
    try {
      const p = await navigator.permissions.query({ name: "microphone" });
      setMic(p.state);
      p.onchange = () => setMic(p.state);
    } catch (e) { setMic("desconocido"); }
  }, []);

  const askMic = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      setMic("granted");
    } catch (e) { setMic("denied"); }
  }, []);

  // H13 (Ticket 4b-2): la pista remota (voz del CLIENTE) también se guarda en estado para que
  // useRemoteAudioLevel la analice (AnalyserNode) y mueva las barras de verdad, sin tocar el <audio> ya existente.
  const [remoteStream, setRemoteStream] = useState(null);
  const clientLevel = useRemoteAudioLevel(remoteStream);

  const attachRemote = useCallback((session) => {
    const sdh = session.sessionDescriptionHandler;
    const stream = sdh?.remoteMediaStream;
    if (audioRef.current && stream) {
      audioRef.current.srcObject = stream;
      audioRef.current.play().catch(() => { /* el navegador exige un clic previo; ya hubo uno al marcar */ });
    }
    setRemoteStream(stream || null);
  }, []);

  const onSession = useCallback((session) => {
    sessionRef.current = session;
    session.stateChange.addListener((st) => {
      if (st === SessionState.Established) { setInCall(true); setMuted(false); attachRemote(session); }
      if (st === SessionState.Terminated) {
        if (sessionRef.current === session) sessionRef.current = null;
        setInCall(false); setMuted(false);
        if (audioRef.current) audioRef.current.srcObject = null;
        setRemoteStream(null);
        window.dispatchEvent(new Event("dialer:changed"));
      }
    });
  }, [attachRemote]);

  const setMute = useCallback((flag) => {
    const pc = sessionRef.current?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return false;
    pc.getSenders().forEach((s) => { if (s.track) s.track.enabled = !flag; });
    setMuted(!!flag);
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) { setStatus("apagado"); return undefined; }
    let cancelled = false;
    let ua = null;
    let registerer = null;

    const run = async () => {
      // Solo UNA pestaña tiene el teléfono; las demás esperan y lo toman si esa se cierra.
      if (navigator.locks) {
        setStatus("otra_pestana");
        await new Promise((resolve) => {
          navigator.locks.request(LOCK_NAME, () => new Promise((hold) => { releaseRef.current = hold; resolve(); }));
        });
        if (cancelled) { releaseRef.current?.(); return; }
      }
      try {
        setStatus("conectando");
        const r = await d.get("/phone/config");
        const cfg = r.data;
        if (cancelled) return;
        const mine = cfg?.enabled ? (cfg.stations || [])[0] : null;
        if (!mine) { setStatus("sin_telefono"); setInfo(null); return; }
        setInfo({ extension: mine.extension, station_name: mine.station_name });
        const uri = UserAgent.makeURI(`sip:${mine.extension}@${cfg.domain}`);
        ua = new UserAgent({
          uri,
          authorizationUsername: mine.extension,
          authorizationPassword: mine.password,
          transportOptions: { server: cfg.ws_url },
          logLevel: "error",
          sessionDescriptionHandlerFactoryOptions: { peerConnectionConfiguration: { iceServers: (cfg.stun || []).map((u) => ({ urls: u })) } },
          delegate: {
            onInvite: (invitation) => {
              // La llamada del propio puesto (la origina la central) se contesta sola. Una transferencia o una persona
              // incluida (TRF|/INC| en el nombre) NO: el agente ve quién la manda y decide si la acepta.
              const parts = String(invitation.remoteIdentity?.displayName || "").split("|");
              if (parts[0] === "TRF" || parts[0] === "INC") {
                const item = { kind: parts[0] === "TRF" ? "transferencia" : "incluir", from: parts[1], phone: parts[2], client: parts[3], invitation };
                setIncoming(item);
                invitation.stateChange.addListener((s) => { if (s === SessionState.Terminated) setIncoming((cur) => (cur?.invitation === invitation ? null : cur)); });
                return;
              }
              onSession(invitation);
              invitation.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } })
                .catch((e) => { setError(`No se pudo contestar: ${e?.message || e}`); });
            },
            onDisconnect: () => {
              // Se cayó la conexión con la central: se reconstruye TODO el teléfono (más fiable que reconectar a medias).
              if (cancelled) return;
              setStatus("reconectando");
              const wait = Math.min(2000 * (retryRef.current + 1), 15000);
              retryRef.current += 1;
              setTimeout(() => { if (!cancelled) setRetry((n) => n + 1); }, wait);
            },
          },
        });
        uaRef.current = ua;
        await ua.start();
        registerer = new Registerer(ua);
        registerer.stateChange.addListener((s) => {
          if (cancelled) return;
          if (s === RegistererState.Registered) { setStatus("listo"); setError(""); retryRef.current = 0; }
          if (s === RegistererState.Unregistered) setStatus((cur) => (cur === "reconectando" ? cur : "conectando"));
        });
        await registerer.register();
      } catch (e) {
        if (!cancelled) { setStatus("error"); setError(e?.message || "No se pudo conectar el teléfono"); }
      }
    };
    run();
    refreshMic();

    return () => {
      cancelled = true;
      try { registerer?.unregister(); } catch (e) { /* ya cerrado */ }
      try { ua?.stop(); } catch (e) { /* ya cerrado */ }
      uaRef.current = null;
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, [enabled, onSession, refreshMic, user?.email, retry]);

  // Si el puesto cambia (cobertura recibida o quitada, puesto reasignado), el teléfono se vuelve a armar con el que le toca ahora:
  // un navegador no puede quedarse registrado con una extensión que ya no es suya. Nunca en medio de una llamada.
  const extRef = useRef(null);
  useEffect(() => { extRef.current = info?.extension || null; }, [info]);
  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    const check = async () => {
      if (stop || sessionRef.current) return;
      try {
        const r = await d.get("/phone/config");
        const mine = r.data?.enabled ? (r.data.stations || [])[0] : null;
        if (!stop && !sessionRef.current && (mine?.extension || null) !== extRef.current) setRetry((n) => n + 1);
      } catch (e) { /* sin red: se reintenta en el siguiente ciclo */ }
    };
    const timer = setInterval(check, 30000);
    window.addEventListener("dialer:changed", check);
    return () => { stop = true; clearInterval(timer); window.removeEventListener("dialer:changed", check); };
  }, [enabled]);

  // El invitado a una llamada de tres se despide colgando su propio teléfono.
  const hangup = useCallback(() => {
    const ses = sessionRef.current;
    if (!ses) return;
    try { ses.bye(); } catch (e) { /* ya terminada */ }
  }, []);

  // Ticket 4b-4-bis (bug grave reportado por el Founder: la central ya había colgado -0 canales- pero la
  // sesión SIP.js local quedó "zombie" -- nunca llegó/procesó el BYE, así que `inCall` se quedaba en true
  // para siempre). `forceLocalEnd` NUNCA espera una respuesta de la central: intenta despedirse (bye/dispose)
  // pero, pase lo que pase, limpia el estado local YA MISMO (audio, barras, sesión) -- se usa (a) siempre que
  // el operador pulsa "Colgar y cerrar" y (b) cuando se detecta que el servidor ya dio la llamada por
  // terminada mientras `inCall` seguía en true (ver shouldForceLocalHangup en lib/dialer.js).
  const forceLocalEnd = useCallback(() => {
    const ses = sessionRef.current;
    try { ses?.bye?.(); } catch (e) { /* zombie: ya no hay con quién hablar */ }
    try { ses?.dispose?.(); } catch (e) { /* ya liberada */ }
    sessionRef.current = null;
    setInCall(false);
    setMuted(false);
    setRemoteStream(null);
    if (audioRef.current) audioRef.current.srcObject = null;
  }, []);

  // Aceptar / rechazar una llamada transferida o una persona incluida.
  const acceptIncoming = useCallback(() => {
    const cur = incoming;
    if (!cur) return;
    setIncoming(null);
    onSession(cur.invitation);
    cur.invitation.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } })
      .catch((e) => { setError(`No se pudo contestar: ${e?.message || e}`); });
  }, [incoming, onSession]);
  const rejectIncoming = useCallback(() => {
    const cur = incoming;
    if (!cur) return;
    setIncoming(null);
    try { cur.invitation.reject(); } catch (e) { /* ya terminada */ }
  }, [incoming]);

  // Timbre suave de siempre para "incluirme en una llamada" (INC|) -- H14 no toca este caso, solo transferencias.
  useEffect(() => {
    if (!incoming || incoming.kind === "transferencia") return undefined;
    let ctx = null;
    let timer = null;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const beep = () => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = 440;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
        o.connect(g); g.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 1);
      };
      beep();
      timer = setInterval(beep, 3000);
    } catch (e) { /* sin audio: queda el aviso visual */ }
    return () => { if (timer) clearInterval(timer); try { ctx?.close(); } catch (e) { /* ya cerrado */ } };
  }, [incoming]);

  // H14-bis (corrección: Founder/gerente probaron con central real -- durante la CONSULTA el destino nunca
  // recibe `incoming` de SIP.js, solo GET /transfers/incoming ya decía "en consulta" dentro del teléfono). Se
  // lee AQUÍ, en el contexto, independiente de `inCall`/mic/si el SIP ya registró -- es solo una consulta HTTP
  // de lectura, la misma que ya hacía PhoneDevice para su aviso chico; la alerta grande no debe depender de
  // que la invitación SIP llegue para avisar que YA te están transfiriendo un cliente.
  const [transferPreview, setTransferPreview] = useState(null);
  useEffect(() => {
    if (!enabled) { setTransferPreview(null); return undefined; }
    let alive = true;
    const tick = async () => {
      try { const r = await d.get("/transfers/incoming"); if (alive) setTransferPreview(pickTransferPreview(r.data?.items, inCall)); }
      catch (e) { if (alive) setTransferPreview(null); }
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(id); };
  }, [enabled, inCall]);

  // H14 (Ticket 4b-2): timbre FUERTE y repetido mientras la alerta grande de transferencia esté visible. PARA
  // sola al atender/rechazar/colgar (incoming pasa a null) o si la transferencia desaparece sola (otro agente
  // ya la atendió, p. ej.). Si el navegador bloquea el audio por falta de gesto previo, reintenta en el primer
  // clic de la pantalla (sin pedir nada extra al usuario).
  const sirenRef = useRef(null);
  if (!sirenRef.current) sirenRef.current = createSiren(() => { try { return new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { return null; } });
  // Cualquiera de las dos señales muestra la MISMA alerta (nunca dos a la vez: solo una franja de esta forma
  // puede ser verdad al mismo tiempo para este puesto). Si luego SÍ llega la invitación SIP real, `incoming`
  // manda (trae el objeto real para aceptar/rechazar); mientras tanto, se avisa igual con lo que ya hay.
  const showTransferAlert = shouldShowTransferAlert(incoming) || !!transferPreview;
  useEffect(() => {
    const siren = sirenRef.current;
    if (showTransferAlert) {
      siren.start();
      const retry = () => siren.isRunning() || siren.start();
      document.addEventListener("click", retry, { once: true });
      return () => { siren.stop(); document.removeEventListener("click", retry); };
    }
    return undefined;
  }, [showTransferAlert]);

  // Título de la pestaña parpadeando mientras dure la alerta -- se restaura solo al cerrarse.
  useEffect(() => {
    if (!showTransferAlert) return undefined;
    const original = document.title;
    let on = false;
    const id = setInterval(() => { on = !on; document.title = on ? "📞 Te transfieren una llamada" : original; }, 900);
    return () => { clearInterval(id); document.title = original; };
  }, [showTransferAlert]);

  const value = {
    centralOk, setCentralOk, enabled, status, info, inCall, muted, setMute, hangup, forceLocalEnd, mic, askMic, error,
    incoming, acceptIncoming, rejectIncoming, clientLevel,
  };
  return (
    <Ctx.Provider value={value}>
      {children}
      {showTransferAlert && (
        <TransferAlertOverlay incoming={incoming} preview={transferPreview} onAccept={acceptIncoming} onReject={rejectIncoming} />
      )}
      <audio ref={audioRef} autoPlay data-testid="phone-remote-audio" style={{ display: "none" }} />
    </Ctx.Provider>
  );
}
